import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ProfileMenuHeaderComponent } from '@brand-lolly/app/sidenav/sidenav/profile-menu-header.component';
import { IconComponent, TypographyComponent } from 'components';

@Component({
	standalone: true,
	selector: 'app-burger-menu',
	template: `
		<app-profile-menu-header
			[fullName]="fullName"
			[email]="email"
			[image]="image"
			[imageLoading]="imageLoading"
			[largeSize]="true"
		></app-profile-menu-header>

		<div class="items">
			<!-- Campaigns first, matching the sidenav: the aggregate is where a
			brand starts, and Discovery is one of the things that feeds it. The
			emitted targets keep their old names — they are internal switch keys,
			not labels, and renaming them buys nothing. -->
			<div class="item" (click)="navigate.emit('projects')">
				<app-icon type="svg" [size]="20" color="#969696">
					nav_ai_projects
				</app-icon>
				<typography variant="p/base_medium">Campaigns</typography>
			</div>

			<div class="item" (click)="navigate.emit('ai_agent')">
				<app-icon type="svg" [size]="20" color="#969696">
					nav_ai_agent
				</app-icon>
				<typography variant="p/base_medium">Discovery</typography>
			</div>

			<div class="item" (click)="navigate.emit('partner-ads')">
				<app-icon type="svg" [size]="20" color="#969696">
					meta_mentions
				</app-icon>
				<typography variant="p/base_medium">Partner Ads</typography>
			</div>

			<div class="item" (click)="navigate.emit('messaging')">
				<app-icon type="svg" [size]="20" color="#969696">
					nav_messaging
				</app-icon>
				<typography variant="p/base_medium">Messaging</typography>
			</div>

			<div class="item" (click)="goToSettings.emit()">
				<app-icon type="svg" [size]="20" color="#969696">
					regular_settings
				</app-icon>
				<typography variant="p/base_medium">Settings</typography>
			</div>

			<!-- Only the account owner changes what the account pays, so a
			     seated member is not offered the way in. -->
			<div
				*ngIf="canManageBilling"
				class="item"
				(click)="goToUpgrade.emit()"
			>
				<app-icon type="svg" [size]="20" color="#969696">
					dollar
				</app-icon>
				<typography variant="p/base_medium">
					Pricing and plans
				</typography>
			</div>

			<div class="item" (click)="goToTicketCreation.emit()">
				<app-icon type="svg" [size]="20" color="#969696">
					help_circle
				</app-icon>
				<typography variant="p/base_medium">Create a Ticket</typography>
			</div>

			<div class="item logout" (click)="logout.emit()">
				<app-icon type="svg" [size]="20" color="#FF5656">
					logout
				</app-icon>
				<typography variant="p/base_medium">Sign Out</typography>
			</div>
		</div>
	`,
	imports: [
		NgIf,
		ProfileMenuHeaderComponent,
		IconComponent,
		TypographyComponent
	],
	styles: [
		`
			:host {
				display: flex;
				position: fixed;
				inset: 3.25rem 0 0 0;
				background: #fff;
				z-index: 1000;
				flex-direction: column;
				overflow-y: auto;

				.items {
					display: flex;
					flex-direction: column;

					.item {
						padding: 0 1rem;
						height: 3.5rem;
						border-bottom: 1px solid #dcdcdc;
						display: flex;
						align-items: center;
						column-gap: 0.75rem;
						background: #fff;
						cursor: pointer;
						transition: background 150ms;

						&:active {
							background: #fafafa;
						}

						typography {
							color: #7a7a7a;
						}

						&.logout {
							typography {
								color: #ff5656;
							}
						}
					}

					.locked {
						opacity: 0.5;
					}
				}
			}
		`
	]
})
export class BurgerMenuComponent {
	@Input() fullName: string;
	@Input() email: string;
	@Input() image: string;
	@Input() imageLoading: boolean = false;
	@Input() planName: string;
	@Input() loading: boolean = false;
	@Input() isTrial: boolean = false;
	@Input() resetTimerInSeconds: number;
	/** Whether this person may change what the account pays. Owner-only. */
	@Input() canManageBilling: boolean = true;

	@Output()
	goToSettings = new EventEmitter<void>();
	@Output() goToKnowledgeBase = new EventEmitter<void>();
	@Output() goToTicketCreation = new EventEmitter<void>();
	@Output() goToUpgrade = new EventEmitter<void>();
	@Output() logout = new EventEmitter<void>();

	@Output() navigate = new EventEmitter<
		| 'ai_agent'
		| 'projects'
		| 'partner-ads'
		| 'social-listening'
		| 'messaging'
	>();
}
